import _ from "lodash";
import React, { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import {
  AttachmentClipOutlined,
  FilenotfoundColored,
  CloseCircleFilled,
} from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { getThumbnailIcon } from "Utils/AttachmentHelpers";
import { getCommentDisplayContent } from "Utils/RegexHelper";
import handleEnter from "Utils/handleEnterKey";
import { isIdEqual } from "Utils/idHelpers";
import {
  ATTACHMENT_TYPES,
  getFormattedNameForUserType,
  getIsMessagingWidget,
  isMessagingRevempEnabled,
  areMomentsSame,
  MESSAGE_TYPE,
} from "MessagingV2/modules/Utils";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import MediaWrapper, {
  MEDIA_VISIBILITY,
} from "MessagingV2/components/Messages/VisualMedia/MediaWrapper";
import classes from "./ParentMessage.scss";
import ReplyContainerV2 from "./ReplyContainerV2";

const mapAttachmentTitleToType = {
  [ATTACHMENT_TYPES.IMAGE]: "common:photo",
  [ATTACHMENT_TYPES.VIDEO]: "common:video",
  [ATTACHMENT_TYPES.FILE]: "common:file",
  [ATTACHMENT_TYPES.LINK]: "common:link",
};
const FILE_LINK_TYPES = [ATTACHMENT_TYPES.FILE, ATTACHMENT_TYPES.LINK];

const repliedImgResolution = { width: 20, height: 20 };

const FileIcon = ({ attachment }) => {
  const { type, mimeType } = attachment;
  const Icon = getThumbnailIcon({
    type,
    mimeType,
  });

  return React.isValidElement(Icon) ? (
    React.cloneElement(Icon, { size: "xx-small" })
  ) : (
    <FilenotfoundColored size="xx-small" />
  );
};

const LabelText = props => {
  const { label, isDeleted, totalAttachments, singleAttachment } = props;
  const { t } = useI18n(["common"]);

  if (isDeleted) return t("common:message_was_deleted");
  if (label) {
    return getCommentDisplayContent(label, {
      getHtml: item => `@${item}`, // without highlighting in parent message
      renderLinksAsAnchor: false,
      newLineReplaceWith: "\n", // keep it as it is
    });
  }

  // If label is not present meaning, this is a attachments only message.

  // In an Ideal scenario, this should never happen, just a fallback
  if (!totalAttachments) return null;
  if (totalAttachments > 1) return t("common:attachments");

  if (
    _.includes(FILE_LINK_TYPES, singleAttachment.type) &&
    singleAttachment.name
  ) {
    return singleAttachment.name;
  }

  const attachmentTitle = _.get(
    mapAttachmentTitleToType,
    singleAttachment.type,
    null
  );

  return attachmentTitle ? t(attachmentTitle) : null;
};

const WrapWithBrackets = ({ children }) => <>[{children}]</>;

const ParentMessage = props => {
  const {
    parentMessage,
    isChildMessageSentByUser,
    goToParentMessage,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isPlainContainer,
    isClosable,
    onCloseClick,
    isFromChatBar = false,
  } = props;

  const { t } = useI18n(["common"]);
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const {
    screenMode,
    activeUserId: userId,
    isPreviewMessagesModal,
  } = useChatOptions();

  const {
    label,
    createdBy,
    attachments,
    isDeleted: isParentMessageDeleted = false,
    createdAt: parentMessageCreatedAt,
    updatedAt: parentMessageUpdatedAt,
    type: parentMessageType,
  } = parentMessage;

  const isParentMessageEdited =
    !!parentMessageUpdatedAt &&
    !areMomentsSame({
      date1: parentMessageCreatedAt,
      date2: parentMessageUpdatedAt,
    });

  const totalAttachments = _.size(attachments);

  const { isDeleted: isUserDeleted, isArchived: isUserArchived } = createdBy;

  const isParentMessageSentByUser = isIdEqual(_.get(createdBy, "id"), userId);
  const isMessagingWidget = getIsMessagingWidget({ screenMode });

  const onClickMessage = e => {
    e.stopPropagation();
    goToParentMessage(parentMessage);
  };

  const handleCloseClick = e => {
    e.stopPropagation();
    onCloseClick(e);
  };

  const containerClasses = classNames(classes.container, {
    [classes.plainContainer]: isPlainContainer,
    [classes.senderContainer]: !isPlainContainer && isChildMessageSentByUser,
    [classes.receiverContainer]: !isPlainContainer && !isChildMessageSentByUser,
  });

  const isUserActive = !isUserDeleted && !isUserArchived;
  const nameClasses = classNames("text-label-3", {
    "text-textLink": isUserActive && isChildMessageSentByUser,
    "text-textSubtle": isUserActive && !isChildMessageSentByUser,
    "text-textDefault": !isUserActive,
  });

  const isSingleLineLabel = !!(isMessagingWidget || totalAttachments);
  const labelClasses = classNames("text-body-2", classes.label, {
    "text-textSubtle": isParentMessageDeleted,
    "text-textDefault": !isParentMessageDeleted,
    [classes.singleLineLabel]: isSingleLineLabel,
    [classes.doubleLineLabel]: !isSingleLineLabel,
  });

  const firstAttachment = _.first(attachments);

  const suffix = isUserDeleted
    ? "common:deleted_user"
    : isUserArchived
    ? "common:archived"
    : null;

  const isForwardedMessage = _.isEqual(
    parentMessageType,
    MESSAGE_TYPE.FORWARDED_MESSAGE
  );
  return (
    <div
      tabIndex={isPreviewMessagesModal ? undefined : 0}
      role={isPreviewMessagesModal ? undefined : "button"}
      onClick={
        isPreviewMessagesModal || isForwardedMessage
          ? undefined
          : onClickMessage
      }
      onKeyDown={
        isPreviewMessagesModal || isForwardedMessage
          ? undefined
          : handleEnter(onClickMessage)
      }
      className={isUIRevempEnabled ? classes.containerV2 : containerClasses}
    >
      {isUIRevempEnabled ? (
        <ReplyContainerV2
          createdBy={createdBy}
          label={label}
          isParentMessageSentByUser={isParentMessageSentByUser}
          isParentMessageDeleted={isParentMessageDeleted}
          totalAttachments={totalAttachments}
          attachments={attachments}
          isFromChatBar={isFromChatBar}
          isParentMessageEdited={isParentMessageEdited}
          parentMessageType={parentMessageType}
        />
      ) : (
        <>
          <div>
            <div className={nameClasses}>
              {isParentMessageSentByUser ? (
                t("common:you")
              ) : (
                <div className={classes.nameContainer}>
                  <div className={classes.name}>
                    {getFormattedNameForUserType({
                      user: createdBy,
                      orderBy: defaultNameSortPreference,
                      defaultStudentNameOrderPreference,
                      isFormattedNameV2: true,
                    })}
                  </div>
                  {suffix ? (
                    <div
                      className={classNames(
                        classes.nameSuffix,
                        "text-textSubtle"
                      )}
                    >
                      <WrapWithBrackets>{t(suffix)}</WrapWithBrackets>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <div className={labelClasses}>
              <LabelText
                label={label}
                isDeleted={isParentMessageDeleted}
                singleAttachment={firstAttachment}
                totalAttachments={totalAttachments}
              />
            </div>
          </div>
          {!isParentMessageDeleted && totalAttachments ? (
            <div className={classes.iconContainer}>
              {totalAttachments > 1 ? (
                <AttachmentClipOutlined size="xx-small" variant="subtle" />
              ) : firstAttachment.type === ATTACHMENT_TYPES.IMAGE ? (
                <MediaWrapper
                  width={40}
                  height={40}
                  imgClassName={classes.imgIcon}
                  attachment={firstAttachment}
                  overlayVisibility={MEDIA_VISIBILITY.HIDDEN}
                  iconVisibility={MEDIA_VISIBILITY.HIDDEN}
                  thumbnailResolution={repliedImgResolution}
                />
              ) : (
                <FileIcon attachment={firstAttachment} />
              )}
            </div>
          ) : null}
        </>
      )}

      {isClosable ? (
        <IconButton
          size="medium"
          variant="plain"
          className={classes.closeIcon}
          onClick={handleCloseClick}
          icon={<CloseCircleFilled />}
        />
      ) : null}
    </div>
  );
};

ParentMessage.propTypes = {
  goToParentMessage: PropTypes.func.isRequired,
  isChildMessageSentByUser: PropTypes.bool.isRequired,
  parentMessage: PropTypes.object.isRequired,
  defaultNameSortPreference: PropTypes.string.isRequired,
  isClosable: PropTypes.bool,
  isPlainContainer: PropTypes.bool,
  onCloseClick: PropTypes.func,
};

ParentMessage.defaultProps = {
  isPlainContainer: false,
  isClosable: false,
  isFromChatBar: false,
  onCloseClick: _.noop,
};

const mapStateToProps = state => ({
  defaultNameSortPreference: getDefaultNameSortPreference(state),
  defaultStudentNameOrderPreference: getDefaultStudentNameOrderPreference(
    state
  ),
});

export default compose(connect(mapStateToProps), memo)(ParentMessage);
